import {
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Switch,
} from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import SelectDropdownLgIcon from "../../assets/icons/SelectDropdownLgIcon";
import {
  quillToolbarFormats,
  quillToolbarModules,
} from "../../constants/config";
import {
  dateFormat,
  dbDateFormat,
  dbTimeFormat,
  employeeTypeList,
  timeFormat,
} from "../../constants/constants";
import {
  useCreateAnnouncementMutation,
  useGetAnnouncementDetailsQuery,
  useUpdateAnnouncementMutation,
} from "../../features/announcement/announcementApi";
import { useGetDepartmentsQuery } from "../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../features/employee/employeeApi";
import useError from "../../hooks/useError";
import { disablePastDates, formatDate, formatTime } from "../../lib/time";
import { toast } from "../../lib/toast";
import { ModalComponentProps } from "../../types";
import { selectSearchOption } from "../../utils/utils";
import EmployeeFileUpload from "../Employees/EmployeeFileUpload";
import CustomModal from "../ui/CustomModal";
import Loader from "../ui/Loader";
import SelectWithCheckbox from "../ui/SelectWithCheckbox";

type TargetType = "employee" | "department" | "employee_type";

export default function ManageAnnouncement({
  handleClose,
  data,
  type,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const [showHomePage, setShowHomePage] = useState(true);
  const [isPublished, setIsPublished] = useState(true);
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: { skip: 0, limit: 0, status: "active" },
  });
  const {
    isLoading: isAnnouncementLoading,
    isError: isAnnouncementDetailsError,
    error: announcementDetailsError,
    data: announcementDetails,
  } = useGetAnnouncementDetailsQuery(data || "", {
    skip: !data && type !== "UPDATE",
  });
  const [createAnnouncement, { isLoading, isError, error, isSuccess }] =
    useCreateAnnouncementMutation();
  const [
    updateDepartment,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      error: updateError,
    },
  ] = useUpdateAnnouncementMutation();
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(
    isAnnouncementDetailsError,
    announcementDetailsError,
    "Get announcement data fail"
  );
  useError(isError, error, "Announcement create fail");
  useError(isUpdateError, updateError, "Announcement update fail");

  const scheduledFor = Form.useWatch("scheduled_for", form);
  const target_type = Form.useWatch("target_type", form) as TargetType;
  const start_date = Form.useWatch("start_date", form);

  const detailsData = announcementDetails?.data?.announcement;

  const onFinish = (values: any) => {
    const {
      description,
      scheduled_for,
      start_date,
      end_date,
      target_id_employee,
      target_id_department,
      target_id_type,
      ...rest
    } = values;

    const ids = {
      employee: target_id_employee,
      department: target_id_department,
      employee_type: target_id_type,
    };

    const newValues = {
      ...rest,
      description: description || undefined,
      scheduled_for,
      show_homepage: showHomePage,
      is_published: isPublished,
      status: scheduled_for === "later" ? "scheduled" : "published",
      start_date:
        scheduled_for === "later"
          ? start_date?.format(dbDateFormat)
          : type === "UPDATE" &&
            detailsData?.scheduled_info?.scheduled_for === "now"
          ? detailsData?.scheduled_info?.start_date
          : formatDate(new Date(), dbDateFormat),
      end_date: end_date?.format(dbDateFormat),
      start_time:
        scheduled_for === "later"
          ? dayjs(start_date)?.format(dbTimeFormat)
          : type === "UPDATE" &&
            detailsData?.scheduled_info?.scheduled_for === "now"
          ? detailsData?.scheduled_info?.start_time
          : formatTime(new Date(), dbTimeFormat),
      end_time: dayjs(end_date)?.format(dbTimeFormat),
      target_id: ids[target_type],
    };

    function areDatesEqual(): boolean {
      return newValues?.start_date === newValues?.end_date;
    }

    if (areDatesEqual()) {
      if (newValues?.start_time >= newValues?.end_time) {
        toast("error", "Start time must be earlier than end time.");
        return;
      }
    }

    if (type === "UPDATE" && data) {
      return updateDepartment({
        id: data,
        body: newValues,
      });
    }

    return createAnnouncement(newValues);
  };

  useEffect(() => {
    if (Object.keys(detailsData || {}) && type === "UPDATE") {
      form.setFieldsValue({
        type: detailsData?.type?.toLowerCase(),
        title: detailsData?.title,
        short_description: detailsData?.short_description,
        description: detailsData?.description || undefined,
        is_published: detailsData?.is_published ? true : false,
        show_homepage: detailsData?.show_homepage ? true : false,
        target_type: detailsData?.target?.type,
        scheduled_for: detailsData?.scheduled_info?.scheduled_for,
        start_date: dayjs(
          `${detailsData?.scheduled_info?.start_date}T${detailsData?.scheduled_info?.start_time}`
        ),
        end_date: dayjs(
          `${detailsData?.scheduled_info?.end_date}T${detailsData?.scheduled_info?.end_time}`
        ),
        attachment: detailsData?.attachment || undefined,
      });

      setShowHomePage(detailsData?.show_homepage ? true : false);
      setIsPublished(detailsData?.is_published ? true : false);
    }
  }, [form, detailsData, type]);

  useEffect(() => {
    if (Object.keys(detailsData || {}) && type === "UPDATE") {
      const targetIds =
        detailsData?.target?.type_ids === "all"
          ? []
          : target_type === "employee"
          ? detailsData?.target?.type_ids
              ?.map((item) => item?.business_member_id)
              ?.filter((id) => id)
          : target_type === "department"
          ? detailsData?.target?.type_ids
              ?.map((item) => item?.department_id)
              ?.filter((id) => id)
          : target_type === "employee_type"
          ? detailsData?.target?.type_ids
              ?.map((item) => item?.type)
              ?.filter((id) => id)
          : [];

      form.setFieldsValue({
        [`target_id_${target_type === "employee_type" ? "type" : target_type}`]:
          targetIds?.length ? targetIds : undefined,
      });
    }
  }, [detailsData, detailsData?.target?.type_ids, form, target_type, type]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Announcement created successfully");
      return handleClose();
    }

    if (isUpdateSuccess) {
      toast("success", "Announcement updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess, isUpdateSuccess]);

  const disableEndDates = (current: any) => {
    if (scheduledFor !== "now" && start_date) {
      return (
        current &&
        current <
          moment(start_date?.format(dbDateFormat), dbDateFormat).startOf("day")
      );
    }
    return disablePastDates(current);
  };

  return (
    <CustomModal
      title={`${type === "UPDATE" ? "Update " : ""}Announcement Form`}
      {...rest}
      handleClose={handleClose}
      width={800}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isAnnouncementLoading ? (
          <Loader />
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-x-4">
              <Form.Item
                name="type"
                label="Event Type"
                className="mb-4"
                initialValue="event"
                rules={[
                  {
                    required: true,
                    message: "Please select type",
                  },
                ]}
              >
                <Select
                  placeholder="Select"
                  size="large"
                  showSearch
                  filterOption={selectSearchOption}
                  options={eventList}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>
              <Form.Item
                name="title"
                label="Event Title"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter a title",
                  },
                ]}
              >
                <Input size="large" placeholder="Enter a title" />
              </Form.Item>

              <Form.Item
                name="short_description"
                label="Short Description"
                className="mb-4 col-span-2"
                rules={[
                  {
                    required: true,
                    message: "Please enter short description",
                  },
                ]}
              >
                <Input.TextArea rows={2} placeholder="Short description" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                className="mb-4 col-span-2"
                rules={[
                  {
                    required: true,
                    message: "Please select description",
                  },
                ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                />
              </Form.Item>
              <div className="col-span-2 mt-4">
                <FormItem title="Shows on homepage">
                  <Form.Item name="show_homepage" className="mb-0">
                    <Switch
                      checked={showHomePage}
                      onChange={(checked) => setShowHomePage(checked)}
                    />
                  </Form.Item>
                </FormItem>
                {/* <FormItem title="Is Publish">
                  <Form.Item name="is_published" className="mb-0">
                    <Switch
                      checked={isPublished}
                      onChange={(checked) => setIsPublished(checked)}
                    />
                  </Form.Item>
                </FormItem> */}
                <FormItem
                  className="!items-start"
                  titleClassName="pt-1.5"
                  title="Choose Module"
                >
                  <div>
                    <Form.Item
                      name="target_type"
                      initialValue="all"
                      className="mb-0"
                    >
                      <Radio.Group
                        className="custom-radio !h-auto"
                        size="large"
                      >
                        {targetTypes?.map((el) => (
                          <Radio
                            className="text-header text-sm font-medium mr-3.5"
                            value={el?.value}
                            key={el?.value}
                          >
                            {el.label}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                    {target_type === "employee" ? (
                      <Form.Item
                        name="target_id_employee"
                        className="mt-3 mb-0"
                        rules={[
                          {
                            required: true,
                            message: "Please select employee",
                          },
                        ]}
                      >
                        <SelectWithCheckbox
                          placeholder="Employee"
                          data={employeeData?.data?.employees?.map((el) => ({
                            name: el?.member?.profile?.name,
                            id: el?.id,
                            employeeId: el?.employee_id || "n/a",
                          }))}
                          showId
                        />
                      </Form.Item>
                    ) : target_type === "department" ? (
                      <Form.Item
                        name="target_id_department"
                        className="mt-3 mb-0"
                        rules={[
                          {
                            required: true,
                            message: "Please select department",
                          },
                        ]}
                      >
                        <SelectWithCheckbox
                          placeholder="Department"
                          data={departmentData?.data?.departments?.map(
                            (el) => ({
                              name: el?.name,
                              id: el?.id,
                            })
                          )}
                        />
                      </Form.Item>
                    ) : target_type === "employee_type" ? (
                      <Form.Item
                        name="target_id_type"
                        className="mt-3 mb-0"
                        rules={[
                          {
                            required: true,
                            message: "Please select employee type",
                          },
                        ]}
                      >
                        <SelectWithCheckbox
                          placeholder="Employee Type"
                          data={employeeTypeList?.map((el) => ({
                            name: el?.label,
                            id: el?.value,
                          }))}
                        />
                      </Form.Item>
                    ) : null}
                  </div>
                </FormItem>
                <FormItem
                  className="!items-start"
                  titleClassName="pt-1.5"
                  title="Scheduled for"
                >
                  <div>
                    <Form.Item
                      name="scheduled_for"
                      initialValue="now"
                      className="mb-0"
                    >
                      <Radio.Group
                        className="custom-radio !h-auto"
                        size="large"
                      >
                        {scheduledForList?.map((el) => (
                          <Radio
                            className="text-header text-sm font-medium mr-3.5"
                            value={el?.value}
                            key={el?.value}
                          >
                            {el.label}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                    <div className="grid sm:grid-cols-2 gap-4 mt-2">
                      {scheduledFor !== "now" ? (
                        <Form.Item
                          name="start_date"
                          label="Start Date & Time"
                          className="mb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please select start date & time",
                            },
                          ]}
                        >
                          <DatePicker
                            showTime
                            format={`${dateFormat} | ${timeFormat}`}
                            disabledDate={disablePastDates}
                          />
                        </Form.Item>
                      ) : null}

                      <Form.Item
                        name="end_date"
                        label="End Date & Time"
                        className="mb-0"
                        rules={[
                          {
                            required: true,
                            message: "Please select end date & time",
                          },
                        ]}
                      >
                        <DatePicker
                          showTime
                          format={`${dateFormat} | ${timeFormat}`}
                          disabledDate={disableEndDates}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </FormItem>
                <div className="pt-1" />
                <FormItem
                  className="!items-start"
                  titleClassName="pt-1.5"
                  title="Attachment"
                >
                  <Form.Item
                    name="attachment"
                    // label="NID Front Image"
                    className="mb-4"
                    valuePropName="fileUrl"
                    // rules={[
                    //   {
                    //     required: true,
                    //     message: "Please upload NID photo",
                    //   },
                    // ]}
                  >
                    <EmployeeFileUpload
                      form={form}
                      accept=".png, .jpg, .jpeg, .mp4, .mov, .avi, .mkv, .mp3, .wav, .aac, .pdf"
                      name="attachment"
                      onChange={(url) =>
                        form.setFieldsValue({
                          attachment: url,
                        })
                      }
                    />
                  </Form.Item>
                </FormItem>
              </div>
            </div>

            <Space className="w-full pt-6 sticky bottom-0 bg-white" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
                disabled={isLoading || isUpdateLoading}
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isLoading || isUpdateLoading}
              >
                {type === "UPDATE" ? "Update" : "Save"}
              </Button>
            </Space>
          </>
        )}
      </Form>
    </CustomModal>
  );
}

export const targetTypes = [
  { label: "All", value: "all" },
  { label: "Department", value: "department" },
  { label: "Employee", value: "employee" },
  { label: "Employee Type", value: "employee_type" },
];
const scheduledForList = [
  { label: "Now", value: "now" },
  { label: "Later", value: "later" },
];
const eventList = [
  { label: "Event", value: "event" },
  { label: "Holiday", value: "holiday" },
  { label: "Financial", value: "financial" },
  { label: "Others", value: "others" },
];

interface FormItemProps {
  title: string;
  titleClassName?: string;
  className?: string;
  children: ReactNode;
}

const FormItem = ({
  title,
  titleClassName = "",
  className = "",
  children,
}: FormItemProps) => (
  <div
    className={`grid grid-cols-[220px_auto] items-center gap-3 mb-4 ${className}`}
  >
    <h6
      className={`min-w-[220px] text-body text-base font-medium ${titleClassName}`}
    >
      {title}:
    </h6>
    {children}
  </div>
);
